/******* Angular Resourse *******/
import { Component, OnInit, ViewEncapsulation, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

/******* Core Resourse *******/
import { CoreConfigService } from '@core/services/config.service';
import { UtilsService } from '@core/services/utils.service';
import { AccessTokenService } from '@core/services/access-token.service';
import { AccountSwitchService } from '@core/services/account-switch.service';
import { CoreConfig } from '@core/types';
import { GlobalUIBlockingService } from '@core/services/global-ui-blocking.service';
import { TranslateService } from '@ngx-translate/core';

/******* API Resourse *******/
import { Auth } from '@api';

/******* Plug-In Resourse *******/
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as _ from 'lodash'

/******* Environments Resourse *******/
import { environment } from 'environments/environment';

@Component({
        selector: 'app-twofa',
        templateUrl: './twofa.component.html',
        styleUrls: ['./twofa.component.scss'],
        encapsulation: ViewEncapsulation.None
})
export class TwofaComponent implements OnInit, OnDestroy, AfterViewInit {

        // Public
        public coreConfig: CoreConfig; // Layout Setting Config
        public codePanelForm: UntypedFormGroup; // code Panel Form
        public submitted: boolean = false; // sumbmit flag
        public loading: boolean = false; // button loading flag
        public queryParameter: { [key: string]: any } = {}; // query parameter

        // Private
        private _unsubscribeAll$: Subject<any>; // UnsubscribeAll Observable
        private errorCounting: number = 0;

        /**
         * constructor
         * 
         * @param {Router} _router 
         * @param {ActivatedRoute} _route 
         * @param {FormBuilder} _formBuilder 
         * @param {Auth} _auth 
         * @param {CoreConfigService} _coreConfigService 
         * @param {UtilsService} _utilsService
         * @param {AccessTokenService} _accessTokenService
         * @param {AccountSwitchService} _accountSwitchService
         * @param {GlobalUIBlockingService} _globalUIBlockingService
         */
        constructor(
                private _route: ActivatedRoute,
                private _router: Router,
                private _formBuilder: UntypedFormBuilder,
                private _auth: Auth,
                private _coreConfigService: CoreConfigService,
                private _utilsService: UtilsService,
                private _accessTokenService: AccessTokenService,
                private _accountSwitchService: AccountSwitchService,
                private _globalUIBlockingService: GlobalUIBlockingService,
                private _translateService: TranslateService) {

                // initial observable item container
                this._unsubscribeAll$ = new Subject();

                // Set Blank Page
                this._coreConfigService.setConfig({
                        layout: {
                                footer: { hidden: true },
                                menu: { hidden: true },
                                navbar: { hidden: true },
                                buyNow: false,
                                customizer: false,
                                scrollTop: false,
                                enableLocalStorage: false,
                                skin: "dark",
                                type: "vertical"
                        }
                });

                // Build up the Form
                this.codePanelForm = this._formBuilder.group({
                        code: ['', [Validators.required]]
                });

                this.errorCounting = 2;
        }

        //  Accessors
        // -----------------------------------------------------------------------------------------------------

        /**
         * convenience getter for easy access to form fields
         */
        get f() {
                return this.codePanelForm.controls;
        }

        // Lifecycle hooks
        // -----------------------------------------------------------------------------------------------------

        /**
         * On init
         */
        ngOnInit(): void {

                // Assign Query Data
                this._route.queryParams.subscribe((params) => {
                        try {
                                const passingData = JSON.parse(window.atob(params.token));
                                this.queryParameter = {
                                        userAuth: { ..._.pick(passingData, ['username', 'password']) },
                                        message: passingData['message'],
                                        twoFactorAuth: passingData['twoFactorAuth'].map((method) => {
                                                return {
                                                        text: this._resendMessage(method.factorType),
                                                        icon: this._resendIcon(method.factorType),
                                                        factorType: method.factorType
                                                };
                                        })
                                };
                        }
                        catch (error) {
                                this._utilsService.alert("error", this._translateService.instant('AUTH.PLEASE_SIGN_IN_AGAIN'), this._translateService.instant('AUTH.FAIL_RESOLVE_DATA'));
                                this._utilsService.clearCookie();
                                this._router.navigate(['/login']);
                        }
                });

                // Subscribe to config changes
                this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll$)).subscribe(config => {
                        this.coreConfig = config;
                });
        }

        /**
         * After View initial
         */
        ngAfterViewInit(): void {
                const inputElement: HTMLElement = document.getElementById('twofa-code');
                inputElement.focus();
        }

        /**
         * On Destroy
         */
        ngOnDestroy(): void {
                this._unsubscribeAll$.next();
                this._unsubscribeAll$.complete();
        }

        // Private methods
        // -----------------------------------------------------------------------------------------------------

        /**
         * process resend message
         * 
         * @param type 
         */
        private _resendMessage(type): string {
                switch (type) {
                        case 'EMAIL':
                                return this._translateService.instant('TWO_FACTOR_AUTH.RESEND_EMAIL');
                        case 'SMS':
                                return this._translateService.instant('TWO_FACTOR_AUTH.RESEND_SMS');
                        default:
                                return '';
                }
        }

        /**
         * process resend icon
         * 
         * @param type 
         */
        private _resendIcon(type): string {
                switch (type) {
                        case 'EMAIL':
                                return 'icon-mail';
                        case 'SMS':
                                return 'icon-message-square';
                        default:
                                return '';
                }
        }

        /**
         * Send Auth code email / email 
         * 
         * @param factorType 
         */
        private _sendAuthCode(factorType): void {
                const inputData = { username: this.queryParameter.userAuth.username };
                let request$ = null;
                switch (factorType) {
                        case 'EMAIL':
                                request$ = this._auth.sendingAuthCodeEmail(inputData);
                                break;
                        case 'SMS':
                                request$ = this._auth.sendingAuthCodeSms(inputData);
                                break;
                        default:
                                break;
                }
                if (request$) {
                        this._globalUIBlockingService.start();
                        request$.subscribe(
                                (returnData) => {
                                        this._globalUIBlockingService.stop();
                                        this._utilsService.alert("success", this._translateService.instant('AUTH.RESENT_CODE'), returnData.data.message);
                                },
                                (error) => {
                                        this._globalUIBlockingService.stop();
                                        this._utilsService.alert("error", this._translateService.instant('AUTH.FAIL_RESEND_CODE'), error);
                                }
                        )
                }
        }

        // Public methods
        // -----------------------------------------------------------------------------------------------------

        /**
         * Keypress to control only input number
         * 
         * @param event event object
         * @returns {boolean}
         */
        public numericOnly(event): boolean {
                const charCode = (event.which) ? event.which : event.keyCode;
                if (charCode < 48 || charCode > 57) {
                        return charCode === 13;
                }
                return true;
        }

        /**
         * Resend Method trigger
         * 
         * @param {Event} event
         */
        public resendFunction(event: Event): void {
                event.preventDefault();
                const primaryMethod = this.queryParameter.twoFactorAuth[0];
                this._sendAuthCode(primaryMethod.factorType);
        }

        /**
         * Do Backup Method trigger
         * 
         * @param {Event} event
         */
        public backupFunction(event: Event): void {
                event.preventDefault();
                const backupMethod = this.queryParameter.twoFactorAuth[1];
                if (backupMethod.factorType === 'SMS' || backupMethod.factorType === 'EMAIL')
                        this._sendAuthCode(backupMethod.factorType);
                else if (backupMethod.factorType === 'GA')
                        this._utilsService.alert("success", this._translateService.instant('AUTH.GOOGLE_AUTHENTICATOR'), this._translateService.instant('AUTH.CHECK_2FA_CODE'))
        }

        /**
         * Submit the form
         * 
         * @returns 
         */
        public onSubmit(): void {

                // Submitted set true
                this.submitted = true;

                // stop here if form is invalid
                if (this.codePanelForm.invalid) {
                        return;
                }

                let inputData = {
                        "clientSecret": environment.CLIENT_SECRET,
                        "clientId": environment.CLIENT_ID,
                        username: this.queryParameter.userAuth.username,
                        password: this.queryParameter.userAuth.password,
                        authCode: this.f.code.value
                };

                this.loading = true;
                this._auth.login(inputData).subscribe(
                        (results) => {
                                let returnData = results.data;

                                // call initial Access Token
                                this._accessTokenService.initialAccessToken({
                                        code: returnData.code,
                                        clientId: environment.CLIENT_ID,
                                        username: this.queryParameter.userAuth.username
                                }).subscribe((returnData) => {
                                        this._accountSwitchService.switchAccount(returnData.data); // Switch Account
                                }, () => {
                                        this._utilsService.alert('error', this._translateService.instant('AUTH.FAIL_SIGN_IN'), '');
                                        this.loading = false; // Release Button
                                });
                        },
                        (error) => {
                                // Release Button
                                this.loading = false;
                                if (this.errorCounting <= 0) {
                                        this._utilsService.alert("error", this._translateService.instant('AUTH.FAIL_SIGN_IN'), error);
                                        this._utilsService.clearCookie();
                                        this._router.navigate(['/login']);
                                }
                                else {
                                        this._utilsService.alert("error", this._translateService.instant('AUTH.TWO_FACTOR_AUTH_FAIL'), this._translateService.instant('AUTH.SECURITY_ATTEMPT_WARNING'));
                                        this.errorCounting--;
                                        this.f.code.setValue('');
                                        this.submitted = false;
                                        const inputElement: HTMLElement = document.getElementById('twofa-code');
                                        inputElement.focus();
                                }
                        }
                )
        }
}
